/**
 * Logger Service
 * 
 * Centralized logging interface with a pluggable implementation.
 * - In production (when REACT_APP_APPINSIGHTS_CONNECTION_STRING is set),
 *   telemetry is sent to Azure Application Insights.
 * - Otherwise, falls back to the browser console.
 * 
 * Usage:
 *   import { logger } from '../services/logger';
 *   logger.trackError(error, 'SubscriptionService.create');
 *   logger.trackEvent('SubscriptionCreated', { id: sub.id });
 */

// ─── Logger Interface ────────────────────────────────────────────────

export interface ILogger {
    /** Log an error with optional source context */
    trackError(error: Error | string, source?: string): void;

    /** Log a warning */
    trackWarning(message: string, source?: string): void;

    /** Log an informational event with optional custom properties */
    trackEvent(name: string, properties?: Record<string, unknown>): void;

    /** Log a verbose/debug message */
    trackTrace(message: string, source?: string): void;
}

// ─── Console Logger ──────────────────────────────────────────────────

class ConsoleLogger implements ILogger {
    private formatPrefix(source?: string): string {
        const ts = new Date().toISOString();
        return source ? `[${ts}] [${source}]` : `[${ts}]`;
    }

    trackError(error: Error | string, source?: string): void {
        const prefix = this.formatPrefix(source);
        if (error instanceof Error) {
            console.error(`${prefix} ERROR:`, error.message, error.stack ?? '');
        } else {
            console.error(`${prefix} ERROR:`, error);
        }
    }

    trackWarning(message: string, source?: string): void {
        console.warn(`${this.formatPrefix(source)} WARN:`, message);
    }

    trackEvent(name: string, properties?: Record<string, unknown>): void {
        console.info(`${this.formatPrefix()} EVENT: ${name}`, properties ?? '');
    }

    trackTrace(message: string, source?: string): void {
        console.debug(`${this.formatPrefix(source)} TRACE:`, message);
    }
}

// ─── Singleton Export ────────────────────────────────────────────────
// Uses AppInsightsLogger when a connection string is configured,
// otherwise falls back to ConsoleLogger (dev / local).

import { AppInsightsLogger } from './appInsightsLogger';

const appInsightsConnectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;

export const logger: ILogger = appInsightsConnectionString
    ? new AppInsightsLogger(appInsightsConnectionString)
    : new ConsoleLogger();

export default logger;
